import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgeGroup } from '../../models/age-group.model';
import { RangeField } from '../../models/range-field.model';
import { SpendingGroup } from '../../models/spending-group.model';
import { DataFiltersService } from '../../services/data-filters.service';
import { SystemGroupsService } from '../../services/system-groups.service';
import { ToggleableNameValuePair } from '../../models/name-value-pair.model';
import { IncomeGroup } from '../../models/income-group.model';
import { CategoryService } from '../../services/categories.service';
import { AuthService } from '../../../auth/services/auth.service';
import { flatCategories } from '../../../../utils';
import { Gender } from '../../models/gender.enum';
import { Generations } from '../../models/generations.model';

@Component({
  selector: 'mpro-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
})
export class TagsComponent implements OnInit, OnDestroy {
  public Gender = Gender;
  public spendingTag: { text: string; color: string } = {
    text: 'All spending groups',
    color: 'blue',
  };
  public ageTag: { text: string; color: string } = {
    text: 'All age groups',
    color: 'blue',
  };
  public genderTag: { text: string; color: string } = {
    text: 'Both genders',
    color: 'blue',
  };
  public categoryTag: { text: string; color: string } = {
    text: 'All Categories',
    color: 'blue',
  };
  public generationTag: { text: string; color: string } = {
    text: Generations.ALL,
    color: 'blue',
  };

  @Input() public spending: boolean = true;
  @Input() public age: boolean = true;
  @Input() public gender: boolean = true;
  @Input() public category: boolean = true;
  @Input() public generations: boolean = true;

  private ngUnsubscribe: Subject<void> = new Subject();
  private isFreemium = false;
  private categories: ToggleableNameValuePair[] = [];

  constructor(
    private dataFiltersService: DataFiltersService,
    private systemGroupsService: SystemGroupsService,
    private categoryService: CategoryService,
    private authService: AuthService
  ) {}

  public ngOnInit(): void {
    this.categoryService
      .getUserCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((categories) => (this.categories = categories));
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => {
        this.isFreemium = plan.isFreemium;
      });
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        if (this.spending) {
          this.updateSpendingIncomeTag(filters.spendingGroup);
        }
        if (this.age) {
          this.updateAgeTag(filters.ageGroup);
        }
        if (this.gender) {
          this.updateGenderTag(filters.gender);
        }
        if (this.category) {
          if (!this.isFreemium) {
            this.updateCategoryTag(filters.category);
          } else {
            this.categoryTag.text = filters.freemiumCategory.label;
          }
        }
        if (this.generations) {
          this.updateGenerationTag(filters.generation);
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private updateGenderTag(gender: string | undefined) {
    if (!gender) {
      this.genderTag.text = 'All genders';
      this.genderTag.color = 'blue';
      return;
    }

    if (gender === 'male') {
      this.genderTag.text = 'Male';
      this.genderTag.color = 'teal';
    } else {
      this.genderTag.text = 'Female';
      this.genderTag.color = 'purple';
    }
  }
  private updateGenerationTag(generation: string | undefined) {
    if (!generation || generation === Generations.ALL) {
      this.generationTag.text = '';
      return;
    }

    this.generationTag.text = generation;
    this.generationTag.color = 'blue';
  }

  private updateSpendingIncomeTag(spendingGroup: SpendingGroup | IncomeGroup) {
    const spendingRangeField = new RangeField(
      spendingGroup.greaterThan,
      spendingGroup.lessThan
    );
    this.spendingTag.text = RangeField.getRangeFieldText(
      spendingRangeField,
      `All ${spendingGroup.type.toLocaleLowerCase()} groups`,
      '$'
    );
    this.spendingTag.color = this.systemGroupsService.getSpendingGroupColor(
      spendingGroup.greaterThan,
      spendingGroup.lessThan
    ).name;
  }

  private updateAgeTag(ageGroup: AgeGroup) {
    const ageRangeField = new RangeField(
      ageGroup.greaterThan,
      ageGroup.lessThan
    );
    this.ageTag.text = RangeField.getRangeFieldText(
      ageRangeField,
      'All age groups'
    );
  }

  updateCategoryTag(category: string): void {
    this.categoryTag.text =
      flatCategories(this.categories).find((o) => o.value === category)?.name ||
      'All Categories';
  }
}
